import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * A render throw must not blank the app.
 *
 * WHY THIS EXISTS. `GET /v1/me/score` nests its payload under `score`; the
 * client read it as flat, so `data.breakdown` was undefined, `.length` threw,
 * React unmounted the whole tree and the page went WHITE. Not "the quiz screen
 * failed" — the entire client, including the navigation needed to get away from
 * it. The console held the only evidence.
 *
 * This is a demo client whose job is to show what the api actually returns, so
 * the failure mode has to be legible rather than blank. A boundary per screen
 * keeps a broken screen broken and everything else usable.
 *
 * It deliberately shows the message and stack: this app is demonstrated with the
 * console open, and a caught error that says nothing is barely better than a
 * white page.
 */
interface Props {
  /** Named so the message can say WHICH screen died. */
  screen: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ScreenBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(`[${this.props.screen}] render failed`, error, info.componentStack);
  }

  override componentDidUpdate(prev: Props): void {
    // Navigating away from a broken screen must clear the error, or the
    // boundary would keep showing it over whatever came next.
    if (prev.screen !== this.props.screen && this.state.error !== null) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    return (
      <div className="failure">
        <h2>this screen failed to render</h2>
        <p>
          The api call may well have succeeded — this is a bug in the client, most likely a
          response shape it did not expect. The other screens still work.
        </p>
        <pre>
          {error.name}: {error.message}
          {'\n\n'}
          {error.stack}
        </pre>
      </div>
    );
  }
}
